import { useState } from "react";
import { db } from "../../shared/firebase";
import { useAppSelector } from "../../store/hooks";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { doc, updateDoc } from "firebase/firestore";
import { AiOutlineDelete, AiOutlineEdit } from "react-icons/ai";
import { BiSelectMultiple } from "react-icons/bi";
import { MdOutlineCancel } from "react-icons/md";
import { LazyLoadImage } from "react-lazy-load-image-component";
import WatchlistResult from "../Watchlist/WatchlistResult";
import Sidebar from "../Common/Sidebar";
import Skeleton from "../Common/Skeleton";
import MobileNav from "../Common/MobileNav";

const ListViewForWatchlistAndHistory = ({
  films,
  isLoading,
  pageType,
  genresList,
}) => {
  const currentUser = useAppSelector((state) => state.auth.user);

  const [isEditing, setIsEditing] = useState(false);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [selections, setSelections] = useState([]);
  const [isShowPrompt, setIsShowPrompt] = useState(false);
  const [isSidebarActive, setIsSidebarActive] = useState(false);

  const [listViewCurrentTab, setListViewCurrentTab] = useState(
    localStorage.getItem("ListViewCurrentTab") || "all"
  );

  const [parent] = useAutoAnimate();
  const [action] = useAutoAnimate();
  const [show] = useAutoAnimate();

  const selectAllHandler = () => {
    if (isSelectAll) {
      setSelections([]);
      setIsSelectAll(false);
      return;
    }

    setIsSelectAll(true);
    if (listViewCurrentTab === "all") {
      setSelections(films.map((film) => film.id));
    } else if (listViewCurrentTab === "tv") {
      setSelections(
        films.filter((film) => film.media_type === "tv").map((film) => film.id)
      );
    } else if (listViewCurrentTab === "movie") {
      setSelections(
        films
          .filter((film) => film.media_type === "movie")
          .map((film) => film.id)
      );
    }
  };

  const clearSelection = () => {
    if (!currentUser) return;

    const editedFilms = films.filter(
      (film) => selections.indexOf(film.id) === -1
    );

    updateDoc(doc(db, "users", currentUser?.uid), {
      ...(pageType === "watchlist" && { watchlist: editedFilms.reverse() }),
      ...(pageType === "history" && { recentlyWatch: editedFilms.reverse() }),
    });

    setSelections([]);
    setIsSelectAll(false);
    setIsShowPrompt(false);
  };

  return (
    <>
      <div ref={show}>
        {isShowPrompt && (
          <>
            <div className="fixed top-[35%] md:left-[35%] left-[5%] right-[5%] md:w-[450px] max-w-[90vw] min-h-[100px] px-5 py-4 z-40 bg-dark-lighten rounded-md shadow-md">
              <div className="mx-auto mb-7 h-16 w-16 rounded-full shadow-md shadow-gray-darken bg-gradient-to-t from-transparent to-gray-darken flex items-center justify-center">
                <AiOutlineDelete size={40} className="text-primary " />
              </div>
              <p className="text-white text-lg">
                Do you really want to remove selected{" "}
                {selections.length === 1 ? "film" : "films"} from your{" "}
                {pageType}?
              </p>
              <div className="flex gap-5 mt-8 justify-end">
                <button
                  onClick={clearSelection}
                  className="px-4 py-2 rounded-full bg-dark-lighten-2 text-white hover:bg-white hover:text-dark-darken transition duration-300"
                >
                  Yes
                </button>
                <button
                  onClick={() => setIsShowPrompt(false)}
                  className="px-4 py-2 rounded-full text-white bg-dark-lighten-2 hover:bg-primary  transition duration-300"
                >
                  Cancel
                </button>
              </div>
            </div>
            <div
              onClick={() => setIsShowPrompt(false)}
              className="fixed top-0 left-0 w-full h-full z-30 bg-black/60"
            ></div>
          </>
        )}
      </div>

      <MobileNav setIsSidebarActive={setIsSidebarActive} />

      <div className="md:flex hidden gap-6 items-center absolute top-4 right-5">
        <p>{currentUser?.displayName || "Guest"}</p>
        <LazyLoadImage
          src={currentUser ? currentUser.photoURL : "/defaultAvatar.png"}
          alt="User avatar"
          className="w-10 h-10 rounded-full object-cover"
          effect="opacity"
          referrerPolicy="no-referrer"
        />
      </div>

      <div className="flex min-h-[84vh]">
        <Sidebar
          setIsSidebarActive={setIsSidebarActive}
          isSidebarActive={isSidebarActive}
        />
        <div className="flex-grow px-[15px] md:px-[2vw] pb-16 border-l border-gray-darken">
          <h1 className="text-white text-3xl font-medium uppercase my-8">
            {pageType === "watchlist" ? "My Watchlist" : "Watched Recently"}
          </h1>

          <div
            ref={action}
            className="flex flex-col md:flex-row items-start md:items-end gap-5 md:justify-between m mb-4"
          >
            <div className="inline-flex items-center gap-0 md:gap-[10px] relative">
              <button
                onClick={() => {
                  setListViewCurrentTab("all");
                  localStorage.setItem("listViewCurrentTab", "all");
                }}
                className={`px-3 py-2 w-[100px] rounded-full outline-0 border-0 mr-2 text-sm text-gray-400 transition duration-300 hover:text-white
                  ${
                    listViewCurrentTab === "all" &&
                    "!text-white tw-shaded-button"
                  }`}
              >
                All
              </button>
              <button
                onClick={() => {
                  setListViewCurrentTab("movie");
                  localStorage.setItem("listViewCurrentTab", "movie");
                }}
                className={`px-3 py-2 w-[100px] rounded-full outline-0 border-0 mr-2 text-sm text-gray-400 transition duration-300 hover:text-white
                  ${
                    listViewCurrentTab === "movie" &&
                    "!text-white tw-shaded-button"
                  }`}
              >
                Movie
              </button>
              <button
                onClick={() => {
                  setListViewCurrentTab("tv");
                  localStorage.setItem("listViewCurrentTab", "tv");
                }}
                className={`px-3 py-2 w-[100px] rounded-full outline-0 border-0 mr-2 text-sm text-gray-400 transition duration-300 hover:text-white
                   ${
                     listViewCurrentTab === "tv" &&
                     "!text-white tw-shaded-button"
                   }`}
              >
                TV Show
              </button>
            </div>

            {!isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="self-end text-lg hover:text-white transition duration-300 flex gap-2 items-center"
              >
                <AiOutlineEdit size={25} />
                <p>Edit</p>
              </button>
            )}

            {isEditing && (
              <div className="flex gap-5 self-end">
                <button
                  onClick={selectAllHandler}
                  className={`text-lg hover:text-white transition duration-300 flex gap-2 items-center ${
                    isSelectAll ? "text-white" : "!text-gray-lighten"
                  }`}
                >
                  <BiSelectMultiple size={25} />
                  <p>Select all</p>
                </button>
                <button
                  onClick={() => setIsShowPrompt(true)}
                  disabled={selections.length === 0}
                  className="disabled:text-gray-700 text-lg hover:text-primary transition duration-300 flex gap-2 items-center"
                >
                  <AiOutlineDelete size={25} />
                  <p>Remove</p>
                </button>
                <button
                  onClick={() => setIsEditing(false)}
                  className=" text-lg hover:text-white transition duration-300 flex gap-2 items-center"
                >
                  <MdOutlineCancel size={25} />
                  <p>Cancel</p>
                </button>
              </div>
            )}
          </div>

          <ul
            ref={parent}
            className="flex flex-wrap items-start gap-x-[25px] gap-y-10 pl-2 pt-2"
          >
            {isLoading &&
              [...new Array(6)].map((_, index) => (
                <li key={index}>
                  <Skeleton className="!w-[200px] !h-[300px] shadow-sm rounded-md overflow-hidden" />
                </li>
              ))}
            {listViewCurrentTab === "all" && (
              <WatchlistResult
                films={films}
                genresList={genresList}
                pageType={pageType}
                isEditing={isEditing}
                selections={selections}
                setSelections={setSelections}
                isLoading={isLoading}
              />
            )}
            {listViewCurrentTab === "movie" && (
              <WatchlistResult
                films={films.filter((film) => film.media_type === "movie")}
                genresList={genresList}
                pageType={pageType}
                isEditing={isEditing}
                selections={selections}
                setSelections={setSelections}
                isLoading={isLoading}
              />
            )}
            {listViewCurrentTab === "tv" && (
              <WatchlistResult
                films={films.filter((film) => film.media_type === "tv")}
                genresList={genresList}
                pageType={pageType}
                isEditing={isEditing}
                selections={selections}
                setSelections={setSelections}
                isLoading={isLoading}
              />
            )}
          </ul>
        </div>
      </div>
    </>
  );
};

export default ListViewForWatchlistAndHistory;
